import {Button, Form, Input} from 'antd';
import {useForm} from 'antd/es/form/Form';
import cn from 'classnames';
import React, {
    useEffect, useState, FC,
} from 'react';
import {Link} from 'react-router-dom';

import {WithTooltipError} from 'components/@common/specific/with-tooltip-error/with-tooltip-error';
import {DynamicIcon} from 'components/dynamic-icon';
import {AlertErrorMessage} from 'components/form/alert-error-message/alert-error-message';
import {createRequiredValidator, getButtonNameByProfile} from 'pages/login-page/login-form/login-form.utils';
import {ILoginPageForm} from 'pages/login-page/login-page-types';
import {useLoginFormStateErrors} from 'pages/login-page/use-login-form-state-errors/use-login-form-state-errors';
import {ReactComponent as LockIcon} from 'shared/assets/auth/lock.svg';
import {ReactComponent as UserIcon} from 'shared/assets/auth/user.svg';
import {URL_LOGIN_KERBEROS, URL_VERIFY_2FA_CODE} from 'shared/constants/urls';
import {useAppHistory} from 'shared/hooks/use-app-history';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    authorize,
    EAuthRoutes,
    EXTERNAL_AUTH_PROFILES,
    selectAuthError,
    selectTwoFactorAuthTypeCode,
    selectUseTwoFAVerificationFlag,
    authSlice,
    EAuthProfile,
    ETwoFactorAuthCode,
    get2faCodeValidity,
} from 'store/slices/auth-slice';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';

import 'pages/login-page/login-form/login-form.less';

interface ILoginFormProps {
    profile: EAuthProfile;
    redirectUrl: string | undefined;
}

export const LoginForm: FC<ILoginFormProps> = ({profile, redirectUrl}: ILoginFormProps) => {
    const dispatch = useAppDispatch();
    const [form] = useForm();
    const history = useAppHistory();

    const useTwoFAVerificationFlag = useAppSelector(selectUseTwoFAVerificationFlag);
    const twoFactorAuthTypeCode = useAppSelector(selectTwoFactorAuthTypeCode);
    const error = useAppSelector(selectAuthError);
    const isLoading = useAppSelector(s => selectIsThunkPending(s, authorize.typePrefix));

    const [animationClassName, setAnimationClassName] = useState<string | undefined>();

    const {
        fieldsErrors,
        resetErrorByFieldName,
        setErrorByFieldName,
    } = useLoginFormStateErrors({
        username: '',
        password: '',
        twoFactorAuthCode: '',
    });

    const isKerberos = profile === EAuthProfile.KERBEROS;
    const isPortal2faAuth = twoFactorAuthTypeCode === ETwoFactorAuthCode.PORTAL;

    useEffect(() => {
        let animationIsCanceled = false;

        if (error?.message) {
            setAnimationClassName('login-form__animation');

            setTimeout(() => {
                if (!animationIsCanceled) {
                    setAnimationClassName(undefined);
                }
            }, 500);
        }

        return () => {
            animationIsCanceled = true;
        };
    }, [error]);

    const handleSubmit = async (data: ILoginPageForm) => {
        const res = await dispatch(authorize({...data, isPortal2faAuth}));

        if (isPortal2faAuth && res?.payload?.code === 200) {
            await dispatch(get2faCodeValidity());
            history.push(URL_VERIFY_2FA_CODE);
        }
    };

    const handleChange = () => {
        if (error?.message) {
            const passwordValue = form.getFieldValue('password');

            // если длина passwordValue меньше 1, поскольку поле обязательное,
            // появится ошибка от antd под полем ввода пароля.
            // ошибка из стейта (error) также выводится под полем ввода пароля,
            // поэтому, во избежание скачков ошибок, анимируем исчезновение ошибки стейта
            // только когда нет ошибки пароля, иначе сразу убираем ее
            if (passwordValue && passwordValue?.length > 1) {
                setAnimationClassName('login-form__animation-off');
                setTimeout(() => {
                    dispatch(authSlice.actions.resetError());
                }, 300);
            } else {
                dispatch(authSlice.actions.resetError());
            }
        }
    };

    return (
        <Form
            className="login-form"
            form={form}
            onFinish={handleSubmit}
            onChange={handleChange}
        >
            <h1 className="login-form__header">
                Авторизация
            </h1>
            <div className="login-form__fields">
                <WithTooltipError
                    className="login-form-tooltip"
                    errors={fieldsErrors.username}
                    visible={!!fieldsErrors.username}
                    withExclamation
                >
                    <Form.Item
                        name="username"
                        rules={[
                            createRequiredValidator({
                                onResolve: () => resetErrorByFieldName('username'),
                                onReject: () => setErrorByFieldName('username', 'Введите имя пользователя'),
                            }),
                        ]}
                    >
                        <Input
                            disabled={isLoading}
                            prefix={<UserIcon />}
                            placeholder="Пользователь"
                        />
                    </Form.Item>
                </WithTooltipError>
                <WithTooltipError
                    className="login-form-tooltip"
                    errors={fieldsErrors.password}
                    visible={!!fieldsErrors.password}
                    withExclamation
                >
                    <Form.Item
                        name="password"
                        rules={[
                            createRequiredValidator({
                                onResolve: () => resetErrorByFieldName('password'),
                                onReject: () => setErrorByFieldName('password', 'Введите пароль'),
                            }),
                        ]}
                    >
                        <Input.Password
                            disabled={isLoading}
                            prefix={<LockIcon />}
                            type="password"
                            placeholder="Пароль"
                        />
                    </Form.Item>
                </WithTooltipError>
                {(useTwoFAVerificationFlag && !isPortal2faAuth) && (
                    <WithTooltipError
                        className="login-form-tooltip"
                        errors={fieldsErrors.twoFactorAuthCode}
                        visible={!!fieldsErrors.twoFactorAuthCode}
                        withExclamation
                    >
                        <Form.Item
                            name="twoFactorAuthCode"
                            rules={[
                                createRequiredValidator({
                                    onResolve: () => resetErrorByFieldName('twoFactorAuthCode'),
                                    onReject: () => setErrorByFieldName('twoFactorAuthCode', 'Введите пароль 2fa'),
                                }),
                            ]}
                        >
                            <Input
                                disabled={isLoading}
                                prefix={<LockIcon />}
                                type="password"
                                placeholder="Пароль 2fa"
                            />
                        </Form.Item>
                    </WithTooltipError>

                )}
                {!!error?.message && (
                    <div className={cn('login-form__error', animationClassName)}>
                        <DynamicIcon
                            type="ExclamationCircleOutlined"
                        />
                        {error.message}
                    </div>
                )}
                <div className="login-form__buttons-container">
                    <Button
                        htmlType="submit"
                        type="primary"
                        block
                        loading={isLoading}
                    >
                        {isPortal2faAuth ? 'Получить код 2fa на почту' : 'Войти'}
                    </Button>
                    {(() => {
                        if (!EXTERNAL_AUTH_PROFILES.includes(profile) && !isKerberos) {
                            return undefined;
                        }

                        if (!redirectUrl && !isKerberos) {
                            const message = (
                                `Невозможно войти через ${profile}, сервер вернул некорректную ссылку для перехода`
                            );
                            return <AlertErrorMessage message={message} />;
                        }

                        return (
                            <Button
                                type="primary"
                                block
                                loading={isLoading}
                                onClick={() => {
                                    if (isKerberos) {
                                        history.push(URL_LOGIN_KERBEROS);
                                    }
                                    if (redirectUrl) {
                                        window.location.replace(redirectUrl);
                                    }
                                }}
                            >{getButtonNameByProfile(profile)}
                            </Button>
                        );
                    })()}
                </div>
                {(useTwoFAVerificationFlag && !isPortal2faAuth) && (
                    <Link
                        className="login-form__link"
                        to={EAuthRoutes.GET_TWO_FA_CODE}
                    >
                        Нет подключения к 2fa
                    </Link>
                )}
            </div>
        </Form>
    );
};
